import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { quotationsAPI, suppliersAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Search, Package, ArrowLeft, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

const CATEGORIES = [
  'Cimento', 'Areia', 'Brita', 'Ferro/Aço', 'Madeira',
  'Tubulação', 'Elétrica', 'Pintura', 'Acabamento', 'Outros',
]

const UNITS = ['kg', 'm³', 'un', 'm²', 'L', 'peça', 'saco', 'm', 'ton']

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  quotationId: string
  existingMaterialIds: string[]
}

function formatCurrency(value: number | null | undefined) {
  if (value == null) return null
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

export function AddMaterialDialog({ open, onOpenChange, quotationId, existingMaterialIds }: Props) {
  const queryClient = useQueryClient()

  // ── Step 1: Search ───────────────────────────────────────────────────────────
  const [mode, setMode] = useState<'search' | 'create'>('search')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<any>(null)
  const [quantity, setQuantity] = useState('1')

  // ── Step 2: Create ───────────────────────────────────────────────────────────
  const [newName, setNewName] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [newUnit, setNewUnit] = useState('')
  const [newPrice, setNewPrice] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['materials-catalog', search],
    queryFn: () => suppliersAPI.listMaterials({ search: search || undefined, limit: 50 }),
    enabled: open && mode === 'search',
  })

  const rawList = data?.data || data || []
  const materials = Array.isArray(rawList)
    ? rawList.filter((m: any) => !existingMaterialIds.includes(m.id))
    : []

  // Mutation: add existing material to QC
  const addMutation = useMutation({
    mutationFn: () =>
      quotationsAPI.addMaterial(quotationId, {
        materialId: selected.id,
        quantity: parseFloat(quantity),
      }),
    onSuccess: () => {
      toast.success(`${selected.name} adicionado.`)
      queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] })
      handleClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // Mutation: create new material then add to QC
  const createAndAddMutation = useMutation({
    mutationFn: async () => {
      const material = await suppliersAPI.createMaterial({
        name: newName.trim(),
        category: newCategory,
        unit: newUnit,
        currentPrice: parseFloat(newPrice),
      })
      await quotationsAPI.addMaterial(quotationId, {
        materialId: material.id,
        quantity: parseFloat(quantity),
      })
      return material
    },
    onSuccess: (material: any) => {
      toast.success(`${material.name} criado e adicionado.`)
      queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] })
      queryClient.invalidateQueries({ queryKey: ['materials-catalog'] })
      handleClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleClose = () => {
    setMode('search')
    setSelected(null)
    setQuantity('1')
    setSearch('')
    setNewName('')
    setNewCategory('')
    setNewUnit('')
    setNewPrice('')
    onOpenChange(false)
  }

  const goToCreate = () => {
    setNewName(search)
    setSelected(null)
    setMode('create')
  }

  const canCreate =
    newName.trim().length >= 2 &&
    newCategory &&
    newUnit &&
    parseFloat(newPrice) > 0 &&
    parseFloat(quantity) > 0

  // ── Render: Search ───────────────────────────────────────────────────────────
  if (mode === 'search') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Material</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <Input
                placeholder="Buscar material no catálogo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="max-h-56 overflow-y-auto rounded-md border divide-y">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : materials.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-neutral-500">
                  <Package className="h-8 w-8 text-neutral-300" />
                  <p className="text-sm">
                    {search ? `"${search}" não encontrado no catálogo` : 'Catálogo vazio'}
                  </p>
                </div>
              ) : (
                materials.map((m: any) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelected(m)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-neutral-50 transition-colors',
                      selected?.id === m.id && 'bg-primary/5 ring-1 ring-inset ring-primary/20',
                    )}
                  >
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-neutral-400">
                        {m.unit}{m.category ? ` · ${m.category}` : ''}
                      </p>
                    </div>
                    {formatCurrency(m.currentPrice) && (
                      <span className="ml-4 shrink-0 text-xs text-neutral-500">
                        {formatCurrency(m.currentPrice)}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>

            {/* Create new material link */}
            <button
              type="button"
              onClick={goToCreate}
              className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
            >
              <Plus className="h-4 w-4" />
              {search ? `Criar "${search}" no catálogo` : 'Criar novo material'}
            </button>

            {selected && (
              <div>
                <Label>Quantidade ({selected.unit})</Label>
                <Input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
            <Button
              disabled={!selected || !quantity || parseFloat(quantity) <= 0 || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              {addMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // ── Render: Create ───────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMode('search')}
              className="text-neutral-400 hover:text-neutral-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <DialogTitle>Criar novo material</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Nome *</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nome do material"
              autoFocus
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="new-category">Categoria *</Label>
              <Input
                id="new-category"
                list="category-suggestions"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Digite ou selecione"
                className="mt-1"
              />
              <datalist id="category-suggestions">
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            </div>

            <div>
              <Label htmlFor="new-unit">Unidade *</Label>
              <Input
                id="new-unit"
                list="unit-suggestions"
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                placeholder="kg, m², un..."
                className="mt-1"
              />
              <datalist id="unit-suggestions">
                {UNITS.map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
            </div>
          </div>

          <div>
            <Label>Preço de referência (R$) *</Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              placeholder="0,00"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Quantidade ({newUnit || '—'})</Label>
            <Input
              type="number"
              min="0.001"
              step="0.001"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setMode('search')}>Voltar</Button>
          <Button
            disabled={!canCreate || createAndAddMutation.isPending}
            onClick={() => createAndAddMutation.mutate()}
          >
            {createAndAddMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Criar e adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
